/**
 * A bounded blob-URL cache for auth-gated images.
 *
 * Browsers never send the `Authorization` header on a bare `<img src>`, so an
 * `/api/v1/files/...` crop has to be fetched with the token and wrapped in an
 * object URL first. The graph shows hundreds of faces at once, so the object
 * URLs are shared through one cache instead of one fetch per node, and the
 * cache is bounded: the least-recently-used entry is evicted and revoked when
 * it overflows.
 *
 * The cache owns every object URL it hands out. `release()` revokes all of
 * them — callers do that when the component that owns the cache unmounts.
 */
import { apiClient } from '@/services/api/client'

/** How many object URLs one cache keeps alive at once. */
export const DEFAULT_MAX_ENTRIES = 200

export type AuthedFetch = (url: string) => Promise<Blob | null>

async function fetchWithToken(url: string): Promise<Blob | null> {
  const token = apiClient.getToken()
  const response = await fetch(url, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!response.ok) return null
  return await response.blob()
}

export class AuthedImageCache {
  /** Insertion-ordered, so the first key is the least recently used. */
  private readonly entries = new Map<string, string>()
  private readonly inFlight = new Map<string, Promise<string | null>>()
  private readonly maxEntries: number
  private readonly fetchBlob: AuthedFetch
  private released = false

  constructor(options: { maxEntries?: number; fetchBlob?: AuthedFetch } = {}) {
    this.maxEntries = options.maxEntries ?? DEFAULT_MAX_ENTRIES
    this.fetchBlob = options.fetchBlob ?? fetchWithToken
  }

  /** Number of live object URLs. Never above `maxEntries`. */
  get size(): number {
    return this.entries.size
  }

  /** The cached object URL for *url*, or null when it was never resolved. */
  peek(url: string): string | null {
    const cached = this.entries.get(url)
    if (cached === undefined) return null
    this.touch(url, cached)
    return cached
  }

  /**
   * Resolve *url* to an object URL, fetching it with the bearer token once.
   * Concurrent callers for the same key share one request. Returns null when
   * the fetch fails or the cache was released while it was in flight.
   */
  async resolve(url: string): Promise<string | null> {
    if (this.released) return null
    const cached = this.peek(url)
    if (cached) return cached

    const pending = this.inFlight.get(url)
    if (pending) return await pending

    const request = this.load(url)
    this.inFlight.set(url, request)
    try {
      return await request
    } finally {
      this.inFlight.delete(url)
    }
  }

  private async load(url: string): Promise<string | null> {
    let blob: Blob | null = null
    try {
      blob = await this.fetchBlob(url)
    } catch {
      return null
    }
    if (!blob) return null

    const objectUrl = URL.createObjectURL(blob)
    if (this.released) {
      URL.revokeObjectURL(objectUrl)
      return null
    }
    // A concurrent resolve may have stored the same key already.
    const existing = this.entries.get(url)
    if (existing) {
      URL.revokeObjectURL(objectUrl)
      this.touch(url, existing)
      return existing
    }
    this.entries.set(url, objectUrl)
    this.evictOverflow()
    return objectUrl
  }

  /** Re-insert so the key becomes the most recently used one. */
  private touch(url: string, objectUrl: string): void {
    this.entries.delete(url)
    this.entries.set(url, objectUrl)
  }

  private evictOverflow(): void {
    while (this.entries.size > this.maxEntries) {
      const oldest = this.entries.keys().next()
      if (oldest.done) return
      const objectUrl = this.entries.get(oldest.value)
      this.entries.delete(oldest.value)
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }

  /** Revoke every object URL this cache created. The cache stays unusable. */
  release(): void {
    this.released = true
    for (const objectUrl of this.entries.values()) URL.revokeObjectURL(objectUrl)
    this.entries.clear()
  }
}

/** True for the auth-gated API paths that a bare `<img>` cannot load. */
export function needsAuthedFetch(url: string): boolean {
  return url.startsWith('/api/')
}
